import { Typography } from '@euler/app/components/typography';
import { useDefectiveBadgeColorMap } from '@euler/app/tv/functions/useDefectiveBadgeColorMap';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Icon, IconType } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { dateFromValue } from '@euler/utils/datetime';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import differenceInMonths from 'date-fns/differenceInMonths';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';
import { Subject } from 'rxjs';
import { MaintenancePlanSlider } from './MaintenancePlanSlider';
import { MaintenancePlanItem, MaintenancePlanValueParams } from './types';
import { getDisplayInfo } from './utils';

export const MaintenancePlanCell = memo(
  ({
    item,
    index,
    style,
    onSave,
    onDelete,
    onAddCustomMaintenancePlan,
    onViewInspectionResults,
  }: {
    item: MaintenancePlanItem;
    index: number;
    style?: StyleProp<ViewStyle>;
    onSave?: (
      item: MaintenancePlanItem,
      value: MaintenancePlanValueParams,
    ) => void;
    onDelete?: (
      item: MaintenancePlanItem,
      confirmed$: Subject<MaintenancePlanValueParams | undefined>,
    ) => void;
    onAddCustomMaintenancePlan?: (
      item: MaintenancePlanItem,
      params: MaintenancePlanValueParams,
      confirmed$: Subject<MaintenancePlanValueParams | undefined>,
    ) => void;
    onViewInspectionResults?: (item: MaintenancePlanItem) => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const translateX$ = useCreation(() => new Subject<number>(), []);
    const confirmed$ = useCreation(
      () => new Subject<MaintenancePlanValueParams | undefined>(),
      [],
    );
    const {
      panel,
      task: {
        maintenancePlan: { defaultDefectiveBorderColor, viewDetailTextColor },
      },
    } = theme;
    const colorMap = useDefectiveBadgeColorMap();
    const [selectedValue, setSelectedValue] = useState<
      MaintenancePlanValueParams | undefined
    >(() => {
      return item.planDetail
        ? {
            mileage: item.planDetail.nextServiceMileage,
            date: dateFromValue(item.planDetail.nextServiceDate),
          }
        : undefined;
    });

    const displayInfo = useMemo(
      () => getDisplayInfo(theme, colorMap, selectedValue, item),
      [colorMap, item, selectedValue, theme],
    );

    const handleSave = useCallback(() => {
      onSave?.(
        item,
        selectedValue ?? {
          mileage: item.startParams.currentVehicleMileage,
          date: item.startParams.startDate,
        },
      );
    }, [item, onSave, selectedValue]);

    const handleDelete = useCallback(() => {
      if (item.constructionJob == null && item.planDetail == null) {
        return;
      }
      onDelete?.(item, confirmed$);
    }, [confirmed$, item, onDelete]);

    const handleViewInspectionResults = useCallback(() => {
      onViewInspectionResults?.(item);
    }, [item, onViewInspectionResults]);

    const onChange = usePersistFn((params: MaintenancePlanValueParams) => {
      if (
        params.date.getTime() !== selectedValue?.date.getTime() ||
        params.mileage !== selectedValue?.mileage
      ) {
        setSelectedValue(params);
      }
      const offset = differenceInMonths(
        params.date,
        item.startParams.startDate,
      );
      if (offset >= 13) {
        onAddCustomMaintenancePlan?.(item, params, confirmed$);
      }
    });

    useEffect(() => {
      const sub = confirmed$.subscribe(params => {
        setSelectedValue(params);
        if (params == null) {
          translateX$.next(0);
        }
      });
      return () => sub.unsubscribe();
    }, [confirmed$, translateX$]);

    return (
      <View
        css={`
          border-radius: ${rpx(20)}px;
          background-color: ${panel.bg};
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          border-left-width: ${rpx(12)}px;
          border-left-color: ${item.defectiveLevel != null
            ? colorMap[item.defectiveLevel]
            : defaultDefectiveBorderColor};
          padding-left: ${rpx(20)}px;
        `}
        style={style}
      >
        <View
          css={`
            width: 15%;
            flex-shrink: 0;
            flex-grow: 0;
          `}
        >
          <Typography.Label
            bold
            size={rpx(28)}
            color={panel.titleTextColor}
            allowFontScaling
            adjustsFontSizeToFit
            numberOfLines={1}
          >
            {item.name}
          </Typography.Label>
          {item.defectiveLevel != null && (
            <TouchableOpacity
              css={`
                margin-top: ${rpx(0)}px;
              `}
              onPress={handleViewInspectionResults}
            >
              <Typography.Label
                light
                size={rpx(18)}
                color={viewDetailTextColor}
              >
                {`查看故障详情 >`}
              </Typography.Label>
            </TouchableOpacity>
          )}
          <Typography.Label
            regular
            size={rpx(18)}
            color={displayInfo.color}
            css={`
              margin-top: ${rpx(10)}px;
            `}
          >
            {displayInfo.label}
          </Typography.Label>
        </View>
        <MaintenancePlanSlider
          index={index}
          name={item.name}
          translateX$={translateX$}
          startDate={item.startParams.startDate}
          currentVehicleAge={item.startParams.currentVehicleAge}
          currentVehicleMileage={item.startParams.currentVehicleMileage}
          selectedValue={selectedValue}
          hasConstructionJob={item.constructionJob != null}
          constructionJobStatus={item.constructionJob?.status}
          isConfirmed={
            displayInfo.type === 'deferred' &&
            displayInfo.status === 'confirmed'
          }
          onChange={onChange}
          css={`
            flex: 1;
            margin-left: ${rpx(28)}px;
            margin-top: ${rpx(32)}px;
            margin-bottom: ${rpx(32)}px;
          `}
        />
        <View
          css={`
            align-items: center;
            margin-left: ${rpx(28)}px;
            margin-right: ${rpx(28)}px;
          `}
        >
          <IconButton icon="check" onPress={handleSave} />
          <IconButton
            icon="close"
            onPress={handleDelete}
            css={`
              margin-top: ${rpx(15)}px;
            `}
          />
        </View>
      </View>
    );
  },
);

const IconButton = memo(
  ({
    icon,
    style,
    onPress,
  }: {
    icon: IconType;
    style?: StyleProp<ViewStyle>;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      task: {
        maintenancePlan: { iconColor, iconBgColor },
      },
    } = usePresentationTheme();
    return (
      <TouchableOpacity
        css={`
          width: ${rpx(50)}px;
          height: ${rpx(50)}px;
          border-radius: ${rpx(50) / 2}px;
          justify-content: center;
          align-items: center;
          background-color: ${iconBgColor};
        `}
        style={style}
        onPress={onPress}
      >
        <Icon name={icon} color={iconColor} size={rpx(28)} />
      </TouchableOpacity>
    );
  },
);
